































import IntervalCount from "@/interfaces/IntervalCount";
import MediaValue from "@/interfaces/MediaValue";
import SelectOption from "@/interfaces/SelectOption";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import CustomChart, { CustomChartItem } from "../CustomChart.vue";
import { SlotObject } from "../TimeSlotPicker.vue";
import VMultiSwitcher from "@/components/VMultiSwitcher.vue";
import VSwitchButton from "@/components/VSwitchButton.vue";
import VCheckButton from "@/components/VCheckButton.vue";
import InvestorsMixins from "@/mixins/http/Investor";
import NamedWithTimeIntervals from "@/models/interfaces/NamedWithTimeIntervals";

export interface ComparatorChartFilter {
  date: {
    from: string;
    to: string;
  };
  granularity: string;
  media: {
    radio: boolean;
    tv: boolean;
  };
  seconds: boolean;
  group: number | null;
  timeSlots?: SlotObject[];
  grpTarget?: string;
}

@Component({
  components: {
    CustomChart,
    VMultiSwitcher,
    VSwitchButton,
    VCheckButton,
  },
})
export default class DaysComparatorChart extends Mixins(InvestorsMixins) {
  @Prop() readonly filter!: ComparatorChartFilter;
  @Prop() readonly media!: MediaValue;
  @Prop({ default: false }) readonly stacked?: boolean;
  @Prop({ default: false }) readonly splitMedia?: boolean;
  @Prop({ default: false }) readonly percentage?: boolean;
  @Prop({ default: "bar" }) readonly type?: string;
  @Prop() readonly color?: string;
  @Prop() readonly radioColor?: string;
  @Prop({ default: "wday" }) readonly granularity?: string;
  @Prop({ default: "spots" }) readonly unitOfMeasure!: string;
  @Prop({ default: true }) readonly showLegend?: boolean;
  @Prop({ default: true }) readonly showValueInSeconds?: boolean;
  @Prop({ default: () => [] }) readonly items!: NamedWithTimeIntervals[];

  private _items: NamedWithTimeIntervals[] = [...this.items];
  private _media: MediaValue = Object.assign(
    { radio: true, tv: true },
    this.media
  );
  private chartItems: CustomChartItem[] = [];

  private chartTypeItems: Array<SelectOption> = [
    { id: "bar", text: "Barre" },
    { id: "line", text: "Linea" },
    { id: "pie", text: "Torta", disabled: true },
  ];

  static readonly GRANULARITY_WEEKDAY = "wday";

  get decimals() {
    return this.unitOfMeasure == "grp_over_spots";
  }

  loading = false;

  mounted() {
    this._items = [...this.items];
    this.setChartItems();
  }

  @Watch("items", { deep: true })
  onItemsChanged(items: NamedWithTimeIntervals[]) {
    this._items = [...items];
    this.setChartItems();
  }

  @Watch("media", { deep: true, immediate: true })
  onMediaChanged(media: MediaValue) {
    this._media = Object.assign({}, media);
    this.setChartItems();
  }

  @Watch("unitOfMeasure")
  onUnitChanged() {
    this.setChartItems();
  }

  updateData(items: NamedWithTimeIntervals[]) {
    this._items = items;
    this.setChartItems();
  }

  get unitOfMeasureName(): string {
    switch (this.unitOfMeasure) {
      case "seconds":
        return "Secondi";
      case "spots":
        return "Spot";
      case "grp":
        return "GRP";
      case "grp_over_spots":
        return "GRP / Spot";
      case "audience":
        return "Contatti";
      case "vod":
        return "VOD";
      case "ebiquity_min":
        return "€ (Stima Min.)";
      case "ebiquity_max":
        return "€ (Stima Max.)";
      default:
        return "Spot";
    }
  }

  get disabled(): boolean {
    return false;
  }

  get chartLabels(): string[] {
    let start = this.$moment(this.filter.date.from);
    let end = this.$moment(this.filter.date.to).add(1, "days");

    const labels = new Set();
    const granularity = this.filter.granularity || this.granularity;
    switch (granularity) {
      case DaysComparatorChart.GRANULARITY_WEEKDAY:
        start = start.startOf("week");
        end = end.endOf("week");

        for (let date = start; date.isBefore(end); date.add(1, "days")) {
          const label = date.format("dddd");
          labels.add(label.charAt(0).toUpperCase() + label.slice(1));
        }

        break;

      case "hour":
        start = this.$moment().hour(6).minutes(0);
        end = this.$moment().add(1, "days").hours(5).minutes(59);
        for (let time = start; time.isBefore(end); time.add(1, "hours")) {
          const label = time.format("HH:mm");
          labels.add(label);
        }

        break;

      case "weekperiod":
        labels.add("Week-days");
        labels.add("Week-ends");

        break;
    }

    return [...labels] as string[];
  }

  onChartItemHidden(index: number) {
    this.$emit("item-hidden", index);
  }

  onChartItemVisible(index: number) {
    this.$emit("item-visible", index);
  }

  getPostData() {
    return {
      filter: {
        date_range: [this.filter.date.from, this.filter.date.to],
      },
      granularity: this.filter.granularity || this.granularity,
    };
  }

  private setChartItems() {
    // eslint-disable-next-line
    this.chartItems = [];
    if (this._items) {
      const series = this._items.map((item: any) => {
        if (this.splitMedia && (this.media?.tv || this.media?.radio)) {
          const tvObj = {
            color: this.color,
            label: "TV",
            data: this.buildIntervalDetections(item.timeIntervals, {
              tv: true,
              radio: false,
            }),
          };
          const radioObj = {
            color: this.radioColor,
            label: "Radio",
            data: this.buildIntervalDetections(item.timeIntervals, {
              tv: false,
              radio: true,
            }),
          };

          const ret = [];

          if (this.media?.tv) {
            ret.push(tvObj);
          }

          if (this.media?.radio) {
            ret.push(radioObj);
          }

          return [...ret];
        } else {
          const obj = {
            color: this.color,
            label: item.name,
            data: this.buildIntervalDetections(
              item.timeIntervals,
              this._media ? this._media : { tv: false, radio: true }
            ),
          };
          return [obj];
        }
      });
      this.chartItems = series.flat();
    }
  }

  getIntervalDetections(interval: IntervalCount, selected_media: MediaValue) {
    let value = 0;
    const media = selected_media;
    //const media = this._media || { tv: true, radio: true };
    if (this.unitOfMeasure == "spots") {
      if (media.tv) {
        value += interval.tvDetectionsCount as number;
      }
      if (media.radio) {
        value += interval.radioDetectionsCount as number;
      }
    } else if (this.unitOfMeasure == "seconds") {
      if (media.tv) {
        value += interval.tvSecondsCount as number;
      }
      if (media.radio) {
        value += interval.radioSecondsCount as number;
      }
    } else if (this.unitOfMeasure == "ebiquity_min") {
      if (media.radio) {
        value += (interval.ebiquityMin ?? 0) as number;
      }
    } else if (this.unitOfMeasure == "ebiquity_max") {
      if (media.radio) {
        value += (interval.ebiquityMax ?? 0) as number;
      }
    } else if (this.unitOfMeasure == "grp") {
      if (media.tv) {
        value += interval.grpCount as number;
      }
    } else if (this.unitOfMeasure == "grp_over_spots") {
      if (media.tv) {
        value += Number.parseFloat(
          ((interval.grpCount as number) / interval.tvDetectionsCount).toFixed(
            2
          )
        );
      }
    } else if (this.unitOfMeasure == "audience") {
      if (media.tv) {
        value += interval.audienceCount as number;
      }
    } else if (this.unitOfMeasure == "vod") {
      if (media.tv) {
        value += interval.vodDetection as number;
      }
    }

    return value;
  }

  buildIntervalDetections(
    intervals: IntervalCount[],
    media: MediaValue
  ): number[] {
    let results: number[] = [];
    const granularity = this.filter.granularity || this.granularity;

    if (granularity === DaysComparatorChart.GRANULARITY_WEEKDAY) {
      const daysInIntervals: number[] = intervals.map(
        (interval: IntervalCount) => interval.timeInterval!
      );

      let idxIntervals = 0;

      for (let idx = 0; idx < 7; idx++) {
        const currentIndex = idxIntervals + 1;
        const interval = intervals.find((i) => i.timeInterval === currentIndex);

        if (interval) {
          results.push(this.getIntervalDetections(interval, media));
        } else {
          results.push(0);
        }

        idxIntervals = currentIndex;
      }
    } else {
      results = intervals.map((interval: IntervalCount) => {
        return this.getIntervalDetections(interval, media);
      });
    }

    return results;
  }
}
